<template>
  <div ref="filterRef" class="max-[1199px]:relative max-[1199px]:w-full">
    <button
      type="button"
      class="w-full rounded-lg border border-[#e8ecea] bg-white px-5 py-2 text-left text-sm font-medium text-[#333] shadow-none outline-none md:hidden"
      :class="{ active: filterBlogMobileOpen }"
      @click="toggleMobileFilter"
    >
      Categories
    </button>
    <ul
      class="flex flex-row relative rounded-lg bg-white p-2.5 max-[1199px]:bg-white text-center shadow-[0px_0px_35px_rgba(0,0,0,0.08)] transition-all before:absolute before:right-5 before:top-0 before:border-[7px] before:border-solid before:border-transparent before:border-b-white before:content-[''] before:-translate-y-full max-[1199px]:absolute max-[1199px]:left-0 max-[1199px]:-translate-y-5 max-[1199px]:pointer-events-none max-[1199px]:opacity-0 min-[1200px]:flex min-[1200px]:flex-wrap min-[1200px]:gap-2.5 min-[1200px]:p-0 min-[1200px]:shadow-none min-[1200px]:before:hidden"
      :class="{
        'max-[1199px]:pointer-events-auto max-[1199px]:opacity-100 max-[1199px]:translate-y-2.5':
          filterBlogMobileOpen,
      }"
    >
      <li class="min-[1200px]:mb-0 min-[1200px]:mr-2.5 min-[1200px]:mt-0">
        <ToggleGroup
          v-model="selectedIds"
          type="multiple"
          class="flex flex-wrap gap-1.5"
          @update:model-value="onCategoriesChange"
        >
          <Button
            variant="outline"
            size="sm"
            class="h-auto rounded-full px-3 py-1 text-xs font-medium"
            @click="resetFilter"
          >
            All
          </Button>
          <ToggleGroupItem
            v-for="{ name, id } in filters"
            :key="id"
            :value="String(id)"
            class="rounded-full px-3 py-1 text-xs font-medium"
          >
            {{ name }}
          </ToggleGroupItem>
        </ToggleGroup>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import type { BlogCategory } from "~/slices/blog/types";
import { onClickOutside } from "@vueuse/core";
import { Button } from "~/shared/components/ui/button";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "~/shared/components/ui/toggle-group";

const props = defineProps<{
  filters: BlogCategory[];
}>();

const emit = defineEmits<{
  setCategories: [value: string];
}>();

const router = useRouter();
const route = useRoute();

const filterRef = ref<HTMLElement | null>(null);
const filterBlogMobileOpen = ref(false);
const selectedIds = ref<string[]>([]);

function toggleMobileFilter() {
  filterBlogMobileOpen.value = !filterBlogMobileOpen.value;
}

function resetFilter() {
  selectedIds.value = [];
  emit("setCategories", "");
  router.push({
    path: route.path,
    query: { ...route.query, categories: undefined },
  });
}

function onCategoriesChange() {
  const filterString = selectedIds.value.length
    ? selectedIds.value.join(",")
    : "";
  emit("setCategories", filterString);
  router.push({
    path: route.path,
    query: { ...route.query, categories: filterString || undefined },
  });
}

function syncSelectedFromRoute() {
  const q = route.query.categories;
  if (!q) {
    selectedIds.value = [];
    return;
  }
  selectedIds.value = String(q)
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
}

watch(
  () => route.query.categories,
  () => syncSelectedFromRoute(),
  { immediate: false },
);

onMounted(() => {
  syncSelectedFromRoute();
  nextTick(() => {
    if (filterRef.value) {
      onClickOutside(filterRef, () => {
        filterBlogMobileOpen.value = false;
      });
    }
  });
});
</script>
